import { trackMasterEvent, MasterTrackingParams } from '@util/analytics/masterTracking';

interface Manual {
  id?: string;
  title: string;
  url: string;
  type: string;
  // Both shapes tolerated during migration. Schema .transform() normalizes
  // legacy → canonical when data flows through Zod parse, but consumer also
  // accepts raw legacy shape (defense in depth — props may bypass parse).
  fileSize?: number;
  fileSizeBytes?: number;
  pageCount?: number;
  language?: string;
  isPrimary?: boolean;
  sourceUrl?: string;
  sourcedAt?: string;
  addedBy?: string;
  lastVerifiedAt?: string;
  verificationStatus?: 'ok' | 'broken' | 'unverified';
}

interface ShutterCountArticle {
  title: string;
  url: string;
  slug: string;
}

interface ResourcesSectionProps {
  master: {
    id: string;
    slug: string;
    brand: string;
    model: string;
    productType?: string;
    manuals?: Manual[];
  };
  shutterCountArticles?: ShutterCountArticle[];
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DownloadIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-[1.8rem] h-[1.8rem] flex-shrink-0 text-[#C75008]"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-[1.8rem] h-[1.8rem] flex-shrink-0 text-[#242E5F]"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

export default function ResourcesSection({ master, shutterCountArticles }: ResourcesSectionProps) {
  const hasManuals = !!(master.manuals && master.manuals.length > 0);
  const hasArticles = !!(shutterCountArticles && shutterCountArticles.length > 0);

  if (!hasManuals && !hasArticles) {
    return null;
  }

  const colSpanClass = hasManuals && hasArticles ? '' : 'md:col-span-2';

  return (
    <section id="resources" aria-label="Resources and Downloads">
      <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]">
        <h2 className="text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[2rem]">
          Resources &amp; Downloads
          <span className="sr-only"> — {master.brand} {master.model}</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-[3rem]">
          {/* Left column: Manuals & Guides */}
          {hasManuals && (
            <div className={colSpanClass}>
              <h3 className="text-[1.2rem] font-bold tracking-[0.1em] uppercase text-gray-400 mb-[1.4rem]">
                Manuals &amp; Guides
              </h3>
              <ul role="list" aria-label="PDF downloads" className="space-y-[1.2rem]">
                {master.manuals!.map((manual, index) => (
                  <li key={`manual-${index}`} role="listitem">
                    <a
                      href={manual.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="flex items-center gap-[1.2rem] group"
                      onClick={() => {
                        const sizeBytes = manual.fileSizeBytes ?? manual.fileSize;
                        trackMasterEvent('master_manual_download', master as MasterTrackingParams, {
                          section: 'resources',
                          // Stable handle for GA4 — falls back to url for legacy entries
                          // (no id field) until backfill (GearBoard #1151) populates ids.
                          manual_id: manual.id ?? manual.url,
                          manual_id_source: manual.id ? 'canonical' : 'legacy',
                          manual_title: manual.title,
                          manual_type: manual.type,
                          manual_language: manual.language,
                          manual_size_bytes: sizeBytes,
                        });
                      }}
                    >
                      <DownloadIcon />
                      <div>
                        <span className="text-[1.5rem] text-brand-secondary group-hover:underline font-medium">
                          {manual.title}
                        </span>
                        <br />
                        <span className="text-[1.3rem] text-gray-500">
                          {(() => {
                            // Read canonical first, fall back to legacy. Schema .transform()
                            // normalizes when data passes through Zod, but server-rendered
                            // props may bypass the parse step.
                            const bytes = manual.fileSizeBytes ?? manual.fileSize;
                            return bytes ? `PDF · ${formatFileSize(bytes)}` : 'PDF';
                          })()}
                        </span>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Right column: Shutter Count Guides */}
          {hasArticles && (
            <div className={colSpanClass}>
              <h3 className="text-[1.2rem] font-bold tracking-[0.1em] uppercase text-gray-400 mb-[1.4rem]">
                Shutter Count Guides
              </h3>
              <ul role="list" aria-label="Shutter count articles" className="space-y-[1.2rem]">
                {shutterCountArticles!.map((article, index) => (
                  <li key={`article-${index}`} role="listitem">
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-[1.2rem] group"
                      onClick={() =>
                        trackMasterEvent('master_article_click', master as MasterTrackingParams, {
                          section: 'resources',
                          article_title: article.title,
                        })
                      }
                    >
                      <DocumentIcon />
                      <div>
                        <span className="text-[1.5rem] text-brand-secondary group-hover:underline font-medium">
                          {article.title}
                        </span>
                        <br />
                        <span className="text-[1.3rem] text-gray-500">
                          gearfocus.com/blog
                        </span>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
